import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useOfficials, usePoliceStations, useSectionVisibility, type Official, type PoliceStation } from '@/hooks/use-supabase-data'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import {
  Shield,
  Building2,
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
  Home,
} from 'lucide-react'
import { ImageUpload } from './image-upload'

interface OfficialFormState {
  name: string
  designation: string
  phone: string
  email: string
  address: string
  photo: string
  category: 'higher_authority' | 'sub_district' | 'village'
  display_order: string
  is_active: boolean
}

interface PoliceStationFormState {
  name: string
  address: string
  phone: string
  email: string
  map_embed_url: string
  inspector_name: string
  inspector_phone: string
  inspector_email: string
  sub_inspector_name: string
  sub_inspector_phone: string
  sub_inspector_email: string
  display_order: string
  is_active: boolean
}

const defaultOfficialForm: OfficialFormState = {
  name: '',
  designation: '',
  phone: '',
  email: '',
  address: '',
  photo: '',
  category: 'higher_authority',
  display_order: '0',
  is_active: true,
}

const defaultPoliceStationForm: PoliceStationFormState = {
  name: '',
  address: '',
  phone: '',
  email: '',
  map_embed_url: '',
  inspector_name: '',
  inspector_phone: '',
  inspector_email: '',
  sub_inspector_name: '',
  sub_inspector_phone: '',
  sub_inspector_email: '',
  display_order: '0',
  is_active: true,
}

export function OfficialsManager() {
  const { officials: higherOfficials, loading: higherLoading, error: higherError, refetch: refetchHigher } = useOfficials('higher_authority')
  const { officials: subDistrictOfficials, loading: subDistrictLoading, error: subDistrictError, refetch: refetchSubDistrict } = useOfficials('sub_district')
  const { officials: villageOfficials, loading: villageLoading, error: villageError, refetch: refetchVillage } = useOfficials('village')
  const { policeStations, loading: policeLoading, error: policeError, refetch: refetchPolice } = usePoliceStations()
  const { visibility, refetch: refetchVisibility } = useSectionVisibility()

  const [activeTab, setActiveTab] = useState<'higher' | 'sub-district' | 'village' | 'police'>('higher')
  const [isOfficialDialogOpen, setIsOfficialDialogOpen] = useState(false)
  const [isPoliceDialogOpen, setIsPoliceDialogOpen] = useState(false)
  const [editingOfficial, setEditingOfficial] = useState<Official | null>(null)
  const [editingPoliceStation, setEditingPoliceStation] = useState<PoliceStation | null>(null)
  const [officialForm, setOfficialForm] = useState<OfficialFormState>(defaultOfficialForm)
  const [policeForm, setPoliceForm] = useState<PoliceStationFormState>(defaultPoliceStationForm)
  const [saving, setSaving] = useState(false)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState<'error' | 'success' | ''>('')

  const handleOfficialInputChange = (field: keyof OfficialFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setOfficialForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handlePoliceInputChange = (field: keyof PoliceStationFormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPoliceForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleAddOfficial = (category: 'higher_authority' | 'sub_district' | 'village') => {
    setEditingOfficial(null)
    setOfficialForm({ ...defaultOfficialForm, category })
    setIsOfficialDialogOpen(true)
  }

  const handleEditOfficial = (official: Official) => {
    setEditingOfficial(official)
    setOfficialForm({
      name: official.name || '',
      designation: official.designation || '',
      phone: official.phone || '',
      email: official.email || '',
      address: official.address || '',
      photo: official.photo || '',
      category: official.category,
      display_order: String(official.display_order || 0),
      is_active: official.is_active !== false,
    })
    setIsOfficialDialogOpen(true)
  }

  const handleSaveOfficial = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setStatusMessage('')
    setStatusType('')

    try {
      const payload = {
        name: officialForm.name.trim(),
        designation: officialForm.designation.trim(),
        phone: officialForm.phone.trim() || null,
        email: officialForm.email.trim() || null,
        address: officialForm.address.trim() || null,
        photo: officialForm.photo.trim() || null,
        category: officialForm.category,
        display_order: parseInt(officialForm.display_order) || 0,
        is_active: officialForm.is_active,
        updated_at: new Date().toISOString(),
      }

      if (editingOfficial?.id) {
        const { error } = await supabase
          .from('officials')
          .update(payload)
          .eq('id', editingOfficial.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('officials').insert({
          ...payload,
          created_at: new Date().toISOString(),
        })
        if (error) throw error
      }

      setIsOfficialDialogOpen(false)
      setStatusType('success')
      setStatusMessage('Official saved successfully.')
      await refetchHigher()
      await refetchSubDistrict()
      await refetchVillage()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to save official.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteOfficial = async (id: string) => {
    try {
      const { error } = await supabase.from('officials').delete().eq('id', id)
      if (error) throw error
      setStatusType('success')
      setStatusMessage('Official deleted successfully.')
      await refetchHigher()
      await refetchSubDistrict()
      await refetchVillage()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to delete official.')
    }
  }

  const handleAddPoliceStation = () => {
    setEditingPoliceStation(null)
    setPoliceForm(defaultPoliceStationForm)
    setIsPoliceDialogOpen(true)
  }

  const handleEditPoliceStation = (station: PoliceStation) => {
    setEditingPoliceStation(station)
    setPoliceForm({
      name: station.name || '',
      address: station.address || '',
      phone: station.phone || '',
      email: station.email || '',
      map_embed_url: station.map_embed_url || '',
      inspector_name: station.inspector_name || '',
      inspector_phone: station.inspector_phone || '',
      inspector_email: station.inspector_email || '',
      sub_inspector_name: station.sub_inspector_name || '',
      sub_inspector_phone: station.sub_inspector_phone || '',
      sub_inspector_email: station.sub_inspector_email || '',
      display_order: String(station.display_order || 0),
      is_active: station.is_active !== false,
    })
    setIsPoliceDialogOpen(true)
  }

  const handleSavePoliceStation = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setStatusMessage('')
    setStatusType('')

    try {
      const payload = {
        name: policeForm.name.trim(),
        address: policeForm.address.trim(),
        phone: policeForm.phone.trim() || null,
        email: policeForm.email.trim() || null,
        map_embed_url: policeForm.map_embed_url.trim() || null,
        inspector_name: policeForm.inspector_name.trim() || null,
        inspector_phone: policeForm.inspector_phone.trim() || null,
        inspector_email: policeForm.inspector_email.trim() || null,
        sub_inspector_name: policeForm.sub_inspector_name.trim() || null,
        sub_inspector_phone: policeForm.sub_inspector_phone.trim() || null,
        sub_inspector_email: policeForm.sub_inspector_email.trim() || null,
        display_order: parseInt(policeForm.display_order) || 0,
        is_active: policeForm.is_active,
        updated_at: new Date().toISOString(),
      }

      if (editingPoliceStation?.id) {
        const { error } = await supabase
          .from('police_stations')
          .update(payload)
          .eq('id', editingPoliceStation.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('police_stations').insert({
          ...payload,
          created_at: new Date().toISOString(),
        })
        if (error) throw error
      }

      setIsPoliceDialogOpen(false)
      setStatusType('success')
      setStatusMessage('Police station saved successfully.')
      await refetchPolice()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to save police station.')
    } finally {
      setSaving(false)
    }
  }

  const handleDeletePoliceStation = async (id: string) => {
    try {
      const { error } = await supabase.from('police_stations').delete().eq('id', id)
      if (error) throw error
      setStatusType('success')
      setStatusMessage('Police station deleted successfully.')
      await refetchPolice()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to delete police station.')
    }
  }

  const handleToggleOfficialActive = async (official: Official, checked: boolean) => {
    setStatusMessage('')
    setStatusType('')
    try {
      const { error } = await supabase
        .from('officials')
        .update({
          is_active: checked,
          updated_at: new Date().toISOString(),
        })
        .eq('id', official.id)
      if (error) throw error
      setStatusType('success')
      setStatusMessage('Status updated.')
      await refetchHigher()
      await refetchSubDistrict()
      await refetchVillage()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to update official status.')
    }
  }

  const handleSetAllOfficialsActive = async (list: Official[], active: boolean) => {
    const ids = list.map((o) => o.id)
    if (ids.length === 0) return
    setBulkSaving(true)
    setStatusMessage('')
    setStatusType('')
    try {
      const { error } = await supabase
        .from('officials')
        .update({
          is_active: active,
          updated_at: new Date().toISOString(),
        })
        .in('id', ids)
      if (error) throw error
      setStatusType('success')
      setStatusMessage(
        active ? 'All officials in this tab enabled on portal.' : 'All officials in this tab disabled on portal.'
      )
      await refetchHigher()
      await refetchSubDistrict()
      await refetchVillage()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to update all officials.')
    } finally {
      setBulkSaving(false)
    }
  }

  const handleTogglePoliceActive = async (station: PoliceStation, checked: boolean) => {
    setStatusMessage('')
    setStatusType('')
    try {
      const { error } = await supabase
        .from('police_stations')
        .update({
          is_active: checked,
          updated_at: new Date().toISOString(),
        })
        .eq('id', station.id)
      if (error) throw error
      setStatusType('success')
      setStatusMessage('Status updated.')
      await refetchPolice()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to update police station status.')
    }
  }

  const handleSetAllPoliceStationsActive = async (active: boolean) => {
    const ids = policeStations.map((s) => s.id)
    if (ids.length === 0) return
    setBulkSaving(true)
    setStatusMessage('')
    setStatusType('')
    try {
      const { error } = await supabase
        .from('police_stations')
        .update({
          is_active: active,
          updated_at: new Date().toISOString(),
        })
        .in('id', ids)
      if (error) throw error
      setStatusType('success')
      setStatusMessage(
        active ? 'All police stations enabled on portal.' : 'All police stations disabled on portal.'
      )
      await refetchPolice()
    } catch (_err) {
      setStatusType('error')
      setStatusMessage('Failed to update all police stations.')
    } finally {
      setBulkSaving(false)
    }
  }

  const handleToggleVisibility = async (sectionKey: string, isVisible: boolean) => {
    try {
      // First try to update existing record
      const { data: existing, error: checkError } = await supabase
        .from('officials_section_visibility')
        .select('id')
        .eq('section_key', sectionKey)
        .single()

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 is "not found" which is okay, we'll insert
        throw checkError
      }

      if (existing) {
        // Update existing record
        const { error } = await supabase
          .from('officials_section_visibility')
          .update({
            is_visible: isVisible,
            updated_at: new Date().toISOString(),
          })
          .eq('section_key', sectionKey)

        if (error) throw error
      } else {
        // Insert new record
        const { error } = await supabase
          .from('officials_section_visibility')
          .insert({
            section_key: sectionKey,
            is_visible: isVisible,
            updated_at: new Date().toISOString(),
          })

        if (error) throw error
      }

      setStatusType('success')
      setStatusMessage(`Section ${isVisible ? 'shown' : 'hidden'} successfully.`)
      await refetchVisibility()
    } catch (err: any) {
      setStatusType('error')
      setStatusMessage(err?.message || 'Failed to update section visibility. Make sure the database table exists.')
    }
  }

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-500 bg-clip-text text-transparent">
          Know Your Officials
        </h2>
        <p className="text-slate-600 text-lg">Manage district, sub-district, and village officials, and police stations.</p>
      </div>

      {statusMessage && (
        <Alert
          className={
            statusType === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : statusType === 'error'
                ? undefined
                : undefined
          }
          variant={statusType === 'error' ? 'destructive' : 'default'}
        >
          {statusType === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      {/* Section Visibility Toggles */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Section Visibility</CardTitle>
          <CardDescription>Control which sections are visible on the public page</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-sm">Higher Authority</p>
                  <p className="text-xs text-muted-foreground">District officials</p>
                </div>
              </div>
              <Switch
                checked={visibility.higher_authority !== false}
                onCheckedChange={(checked) => handleToggleVisibility('higher_authority', checked)}
              />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Home className="w-5 h-5 text-purple-500" />
                <div>
                  <p className="font-medium text-sm">Village Officials</p>
                  <p className="text-xs text-muted-foreground">Village level</p>
                </div>
              </div>
              <Switch
                checked={visibility.village !== false}
                onCheckedChange={(checked) => handleToggleVisibility('village', checked)}
              />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium text-sm">Sub-District</p>
                  <p className="text-xs text-muted-foreground">SDM, Tehsildar</p>
                </div>
              </div>
              <Switch
                checked={visibility.sub_district !== false}
                onCheckedChange={(checked) => handleToggleVisibility('sub_district', checked)}
              />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-sm">Police Stations</p>
                  <p className="text-xs text-muted-foreground">Police details</p>
                </div>
              </div>
              <Switch
                checked={visibility.police_stations !== false}
                onCheckedChange={(checked) => handleToggleVisibility('police_stations', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('higher')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'higher'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Shield className="w-4 h-4 inline mr-2" />
          Higher Authority
        </button>
        <button
          onClick={() => setActiveTab('sub-district')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'sub-district'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Sub-District
        </button>
        <button
          onClick={() => setActiveTab('village')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'village'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Home className="w-4 h-4 inline mr-2" />
          Village Officials
        </button>
        <button
          onClick={() => setActiveTab('police')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'police'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Users className="w-4 h-4 inline mr-2" />
          Police Stations
        </button>
      </div>

      {/* Higher Authority Officials */}
      {activeTab === 'higher' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Higher Authority Officials ({higherOfficials.length})
                </CardTitle>
                <CardDescription>District Collector, SP, and other senior officials</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
                <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-input bg-muted/30 px-2.5 py-1.5">
                  <Label htmlFor="bulk-officials-higher" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                    {bulkSaving ? 'Updating…' : 'All on portal'}
                  </Label>
                  <Switch
                    id="bulk-officials-higher"
                    checked={
                      higherOfficials.length > 0 &&
                      higherOfficials.every((o) => o.is_active !== false)
                    }
                    disabled={bulkSaving || higherOfficials.length === 0}
                    onCheckedChange={(checked) => void handleSetAllOfficialsActive(higherOfficials, checked)}
                    aria-label="Enable or disable all higher authority officials on the public portal"
                  />
                </div>
                <Button
                  onClick={() => handleAddOfficial('higher_authority')}
                  className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Official
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {higherLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mr-3" />
                Loading officials...
              </div>
            ) : higherOfficials.length === 0 ? (
              <div className="text-center py-10">
                <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No higher authority officials added yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {higherOfficials.map((official) => (
                    <TableRow key={official.id}>
                      <TableCell>
                        <div className="font-medium">{official.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">{official.designation}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {official.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              <span>{official.phone}</span>
                            </div>
                          )}
                          {official.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[200px]">{official.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <Badge
                            className={
                              official.is_active !== false
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-500 border-0'
                                : undefined
                            }
                            variant={official.is_active !== false ? 'default' : 'secondary'}
                          >
                            {official.is_active !== false ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Switch
                            checked={official.is_active !== false}
                            onCheckedChange={(checked) => handleToggleOfficialActive(official, checked)}
                            aria-label={
                              official.is_active !== false ? 'Disable official on portal' : 'Enable official on portal'
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditOfficial(official)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete official?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove this official record.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                                  onClick={() => handleDeleteOfficial(official.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sub-District Officials */}
      {activeTab === 'sub-district' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Sub-District Officials ({subDistrictOfficials.length})
                </CardTitle>
                <CardDescription>SDM, Tehsildar, and other sub-district level officials</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
                <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-input bg-muted/30 px-2.5 py-1.5">
                  <Label htmlFor="bulk-officials-sub" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                    {bulkSaving ? 'Updating…' : 'All on portal'}
                  </Label>
                  <Switch
                    id="bulk-officials-sub"
                    checked={
                      subDistrictOfficials.length > 0 &&
                      subDistrictOfficials.every((o) => o.is_active !== false)
                    }
                    disabled={bulkSaving || subDistrictOfficials.length === 0}
                    onCheckedChange={(checked) => void handleSetAllOfficialsActive(subDistrictOfficials, checked)}
                    aria-label="Enable or disable all sub-district officials on the public portal"
                  />
                </div>
                <Button
                  onClick={() => handleAddOfficial('sub_district')}
                  className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Official
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {subDistrictLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mr-3" />
                Loading officials...
              </div>
            ) : subDistrictOfficials.length === 0 ? (
              <div className="text-center py-10">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No sub-district officials added yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subDistrictOfficials.map((official) => (
                    <TableRow key={official.id}>
                      <TableCell>
                        <div className="font-medium">{official.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">{official.designation}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {official.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              <span>{official.phone}</span>
                            </div>
                          )}
                          {official.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[200px]">{official.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <Badge
                            className={
                              official.is_active !== false
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-500 border-0'
                                : undefined
                            }
                            variant={official.is_active !== false ? 'default' : 'secondary'}
                          >
                            {official.is_active !== false ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Switch
                            checked={official.is_active !== false}
                            onCheckedChange={(checked) => handleToggleOfficialActive(official, checked)}
                            aria-label={
                              official.is_active !== false ? 'Disable official on portal' : 'Enable official on portal'
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditOfficial(official)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete official?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove this official record.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                                  onClick={() => handleDeleteOfficial(official.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Village Officials */}
      {activeTab === 'village' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5" />
                  Village Officials ({villageOfficials.length})
                </CardTitle>
                <CardDescription>Village-level officials and local administration</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
                <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-input bg-muted/30 px-2.5 py-1.5">
                  <Label htmlFor="bulk-officials-village" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                    {bulkSaving ? 'Updating…' : 'All on portal'}
                  </Label>
                  <Switch
                    id="bulk-officials-village"
                    checked={
                      villageOfficials.length > 0 &&
                      villageOfficials.every((o) => o.is_active !== false)
                    }
                    disabled={bulkSaving || villageOfficials.length === 0}
                    onCheckedChange={(checked) => void handleSetAllOfficialsActive(villageOfficials, checked)}
                    aria-label="Enable or disable all village officials on the public portal"
                  />
                </div>
                <Button
                  onClick={() => handleAddOfficial('village')}
                  className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Official
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {villageLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mr-3" />
                Loading officials...
              </div>
            ) : villageOfficials.length === 0 ? (
              <div className="text-center py-10">
                <Home className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No village officials added yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {villageOfficials.map((official) => (
                    <TableRow key={official.id}>
                      <TableCell>
                        <div className="font-medium">{official.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">{official.designation}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {official.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              <span>{official.phone}</span>
                            </div>
                          )}
                          {official.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[200px]">{official.email}</span>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <Badge
                            className={
                              official.is_active !== false
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-500 border-0'
                                : undefined
                            }
                            variant={official.is_active !== false ? 'default' : 'secondary'}
                          >
                            {official.is_active !== false ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Switch
                            checked={official.is_active !== false}
                            onCheckedChange={(checked) => handleToggleOfficialActive(official, checked)}
                            aria-label={
                              official.is_active !== false ? 'Disable official on portal' : 'Enable official on portal'
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditOfficial(official)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete official?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove this official record.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                                  onClick={() => handleDeleteOfficial(official.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Police Stations */}
      {activeTab === 'police' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Police Stations ({policeStations.length})
                </CardTitle>
                <CardDescription>Police station details and official contact information</CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-end">
                <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-input bg-muted/30 px-2.5 py-1.5">
                  <Label htmlFor="bulk-police-stations" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                    {bulkSaving ? 'Updating…' : 'All on portal'}
                  </Label>
                  <Switch
                    id="bulk-police-stations"
                    checked={
                      policeStations.length > 0 &&
                      policeStations.every((s) => s.is_active !== false)
                    }
                    disabled={bulkSaving || policeStations.length === 0}
                    onCheckedChange={(checked) => void handleSetAllPoliceStationsActive(checked)}
                    aria-label="Enable or disable all police stations on the public portal"
                  />
                </div>
                <Button
                  onClick={handleAddPoliceStation}
                  className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Police Station
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {policeLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <RefreshCw className="h-6 w-6 animate-spin mr-3" />
                Loading police stations...
              </div>
            ) : policeStations.length === 0 ? (
              <div className="text-center py-10">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No police stations added yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Station Name</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Inspector</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {policeStations.map((station) => (
                    <TableRow key={station.id}>
                      <TableCell>
                        <div className="font-medium">{station.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground max-w-[200px] truncate">{station.address}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {station.inspector_name || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                          <Badge
                            className={
                              station.is_active !== false
                                ? 'bg-gradient-to-r from-indigo-600 to-purple-500 border-0'
                                : undefined
                            }
                            variant={station.is_active !== false ? 'default' : 'secondary'}
                          >
                            {station.is_active !== false ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Switch
                            checked={station.is_active !== false}
                            onCheckedChange={(checked) => handleTogglePoliceActive(station, checked)}
                            aria-label={
                              station.is_active !== false
                                ? 'Disable police station on portal'
                                : 'Enable police station on portal'
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditPoliceStation(station)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete police station?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently remove this police station record.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600"
                                  onClick={() => handleDeletePoliceStation(station.id)}
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Official Dialog */}
      <Dialog open={isOfficialDialogOpen} onOpenChange={setIsOfficialDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOfficial ? 'Edit Official' : 'Add Official'}</DialogTitle>
            <DialogDescription>Enter details for the official.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveOfficial} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="official-name">Name *</Label>
                <Input
                  id="official-name"
                  value={officialForm.name}
                  onChange={handleOfficialInputChange('name')}
                  placeholder="Rajesh Kumar"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="official-designation">Designation *</Label>
                <Input
                  id="official-designation"
                  value={officialForm.designation}
                  onChange={handleOfficialInputChange('designation')}
                  placeholder="District Collector"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="official-photo">Photo</Label>
              <ImageUpload
                currentImage={officialForm.photo}
                onImageChange={(url) => setOfficialForm(prev => ({ ...prev, photo: url }))}
                folder="officials"
                label=""
                description=""
              />
            </div>

            <div className="flex flex-col gap-3 rounded-lg border border-input bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <Label htmlFor="official-is-active" className="text-base">
                  Show on public portal
                </Label>
                <p className="text-sm text-muted-foreground">
                  Turn off to hide this official from the Know Your Officials page.
                </p>
              </div>
              <Switch
                id="official-is-active"
                checked={officialForm.is_active}
                onCheckedChange={(is_active) => setOfficialForm((prev) => ({ ...prev, is_active }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="official-phone">Phone</Label>
                <Input
                  id="official-phone"
                  value={officialForm.phone}
                  onChange={handleOfficialInputChange('phone')}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="official-email">Email</Label>
                <Input
                  id="official-email"
                  type="email"
                  value={officialForm.email}
                  onChange={handleOfficialInputChange('email')}
                  placeholder="official@district.gov.in"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="official-address">Address</Label>
              <Textarea
                id="official-address"
                value={officialForm.address}
                onChange={handleOfficialInputChange('address')}
                placeholder="Office Address"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="official-category">Category *</Label>
                <select
                  id="official-category"
                  value={officialForm.category}
                  onChange={(e) => setOfficialForm(prev => ({ ...prev, category: e.target.value as 'higher_authority' | 'sub_district' | 'village' }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  required
                >
                  <option value="higher_authority">Higher Authority</option>
                  <option value="sub_district">Sub-District</option>
                  <option value="village">Village</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="official-display-order">Display Order</Label>
                <Input
                  id="official-display-order"
                  type="number"
                  value={officialForm.display_order}
                  onChange={handleOfficialInputChange('display_order')}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsOfficialDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Police Station Dialog */}
      <Dialog open={isPoliceDialogOpen} onOpenChange={setIsPoliceDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPoliceStation ? 'Edit Police Station' : 'Add Police Station'}</DialogTitle>
            <DialogDescription>Enter details for the police station.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSavePoliceStation} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="police-name">Station Name *</Label>
                <Input
                  id="police-name"
                  value={policeForm.name}
                  onChange={handlePoliceInputChange('name')}
                  placeholder="Main Police Station"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="police-display-order">Display Order</Label>
                <Input
                  id="police-display-order"
                  type="number"
                  value={policeForm.display_order}
                  onChange={handlePoliceInputChange('display_order')}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="police-address">Address *</Label>
              <Textarea
                id="police-address"
                value={policeForm.address}
                onChange={handlePoliceInputChange('address')}
                placeholder="Police Station Address"
                rows={2}
                required
              />
            </div>

            <div className="flex flex-col gap-3 rounded-lg border border-input bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <Label htmlFor="police-is-active" className="text-base">
                  Show on public portal
                </Label>
                <p className="text-sm text-muted-foreground">
                  Turn off to hide this station from the Know Your Officials page.
                </p>
              </div>
              <Switch
                id="police-is-active"
                checked={policeForm.is_active}
                onCheckedChange={(is_active) => setPoliceForm((prev) => ({ ...prev, is_active }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="police-phone">Station Phone</Label>
                <Input
                  id="police-phone"
                  value={policeForm.phone}
                  onChange={handlePoliceInputChange('phone')}
                  placeholder="+91 98765 43210"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="police-email">Station Email</Label>
                <Input
                  id="police-email"
                  type="email"
                  value={policeForm.email}
                  onChange={handlePoliceInputChange('email')}
                  placeholder="station@police.gov.in"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="police-map">Google Maps Embed URL</Label>
              <Input
                id="police-map"
                type="url"
                value={policeForm.map_embed_url}
                onChange={handlePoliceInputChange('map_embed_url')}
                placeholder="https://www.google.com/maps/embed?pb=..."
              />
              <p className="text-xs text-muted-foreground">
                Get the embed URL from Google Maps: Share → Embed a map → Copy HTML → Extract src URL
              </p>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Inspector Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inspector-name">Name</Label>
                  <Input
                    id="inspector-name"
                    value={policeForm.inspector_name}
                    onChange={handlePoliceInputChange('inspector_name')}
                    placeholder="Inspector Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inspector-phone">Phone</Label>
                  <Input
                    id="inspector-phone"
                    value={policeForm.inspector_phone}
                    onChange={handlePoliceInputChange('inspector_phone')}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inspector-email">Email</Label>
                  <Input
                    id="inspector-email"
                    type="email"
                    value={policeForm.inspector_email}
                    onChange={handlePoliceInputChange('inspector_email')}
                    placeholder="inspector@police.gov.in"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">Sub-Inspector Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sub-inspector-name">Name</Label>
                  <Input
                    id="sub-inspector-name"
                    value={policeForm.sub_inspector_name}
                    onChange={handlePoliceInputChange('sub_inspector_name')}
                    placeholder="Sub-Inspector Name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sub-inspector-phone">Phone</Label>
                  <Input
                    id="sub-inspector-phone"
                    value={policeForm.sub_inspector_phone}
                    onChange={handlePoliceInputChange('sub_inspector_phone')}
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sub-inspector-email">Email</Label>
                  <Input
                    id="sub-inspector-email"
                    type="email"
                    value={policeForm.sub_inspector_email}
                    onChange={handlePoliceInputChange('sub_inspector_email')}
                    placeholder="subinspector@police.gov.in"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsPoliceDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

